import type { Element, Point } from '@/types';
import { isPointNearBezierCurve } from '@/utils/bezier';

/**
 * Finds the topmost element at a given point.
 * @param point The point to check.
 * @param elements An array of elements to search through.
 * @returns The element at the point, or null if no element is found.
 */
export function getElementAtPoint(
  point: Point,
  elements: Element[]
): Element | null {
  // Search in reverse order to find the topmost element
  for (let i = elements.length - 1; i >= 0; i--) {
    const element = elements[i];
    if (element && isPointInElement(point, element)) {
      return element;
    }
  }
  return null;
}

/**
 * Checks if a point is inside a given element's boundaries.
 * @param point The point to check.
 * @param element The element to check against.
 * @returns True if the point is inside the element, false otherwise.
 */
function isPointInElement(point: Point, element: Element): boolean {
  // Skip hit testing for elements that belong to a group
  if (element.groupId) {
    return false;
  }

  switch (element.type) {
    case 'rectangle':
      return isPointInRectangle(point, element);
    case 'ellipse':
      return isPointInEllipse(point, element);
    case 'diamond':
      return isPointInDiamond(point, element);
    case 'text':
      return isPointInText(point, element);
    case 'line':
      return isPointNearLine(point, element);
    case 'arrow':
      return isPointNearArrow(point, element);
    case 'icon':
      return (
        point.x >= element.x &&
        point.x <= element.x + element.width &&
        point.y >= element.y &&
        point.y <= element.y + element.height
      );
    case 'svg':
      return (
        point.x >= element.x &&
        point.x <= element.x + element.width &&
        point.y >= element.y &&
        point.y <= element.y + element.height
      );
    case 'image':
      return (
        point.x >= element.x &&
        point.x <= element.x + element.width &&
        point.y >= element.y &&
        point.y <= element.y + element.height
      );
    case 'group':
      return isPointInRectangle(point, element);
    default:
      return false;
  }
}

/**
 * Checks if a point is inside a rectangle.
 */
function isPointInRectangle(point: Point, element: any): boolean {
  return (
    point.x >= element.x &&
    point.x <= element.x + element.width &&
    point.y >= element.y &&
    point.y <= element.y + element.height
  );
}

/**
 * Checks if a point is inside an ellipse.
 */
function isPointInEllipse(point: Point, element: any): boolean {
  const cx = element.x + element.radiusX;
  const cy = element.y + element.radiusY;
  const dx = point.x - cx;
  const dy = point.y - cy;
  // Equation for a point in an ellipse: (x^2 / a^2) + (y^2 / b^2) <= 1
  return (
    (dx * dx) / (element.radiusX * element.radiusX) +
      (dy * dy) / (element.radiusY * element.radiusY) <=
    1
  );
}

/**
 * Checks if a point is near a line segment.
 */
function isPointNearLine(point: Point, element: any): boolean {
  const threshold = Math.max(10, element.style.strokeWidth || 1);

  // Check if the line has a bezier curve
  if (element.points && element.points.length > 2) {
    return isPointNearBezierCurve(point, element.points, threshold);
  }

  // Fallback for straight lines or legacy format
  const start = element.points ? element.points[0] : element.startPoint;
  const end = element.points
    ? element.points[element.points.length - 1]
    : element.endPoint;

  if (!start || !end) return false;

  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSq = dx * dx + dy * dy;

  if (lengthSq === 0) {
    // It's a point, check distance to that point
    const distSq = (point.x - start.x) ** 2 + (point.y - start.y) ** 2;
    return distSq <= threshold * threshold;
  }

  // Project point onto the line
  const t = ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSq;
  const clampedT = Math.max(0, Math.min(1, t));

  // Find the closest point on the line segment
  const closestX = start.x + clampedT * dx;
  const closestY = start.y + clampedT * dy;

  // Check distance to the closest point
  const distanceSq = (point.x - closestX) ** 2 + (point.y - closestY) ** 2;

  return distanceSq <= threshold * threshold;
}

/**
 * Checks if a point is inside a text element's bounding box.
 */
function isPointInText(point: Point, element: any): boolean {
  return isPointInRectangle(point, element);
}

/**
 * Checks if a point is inside a diamond shape.
 */
function isPointInDiamond(point: Point, element: any): boolean {
  const cx = element.x + element.width / 2;
  const cy = element.y + element.height / 2;
  const tx = Math.abs(point.x - cx);
  const ty = Math.abs(point.y - cy);
  // Manhattan distance check for diamond shape
  return tx / (element.width / 2) + ty / (element.height / 2) <= 1;
}

/**
 * Checks if a point is near an arrow (same as a line for selection).
 */
function isPointNearArrow(point: Point, element: any): boolean {
  return isPointNearLine(point, element);
}
